import { Component, HostListener, computed, inject, signal } from '@angular/core';
import { I18nService } from '../core/i18n.service';

@Component({
  selector: 'app-scroll-top',
  standalone: true,
  template: `
    <button
      type="button"
      class="scroll-top"
      [class.visible]="visible()"
      [style.--progress]="progressPercent() + '%'"
      (click)="scrollTop()"
      [attr.aria-label]="label()"
    >
      <span>↑</span>
    </button>
  `,
  styles: [`
    .scroll-top {
      --progress: 0%;
      align-items: center;
      background:
        linear-gradient(var(--surface), var(--surface)) padding-box,
        conic-gradient(var(--accent) var(--progress), color-mix(in srgb, var(--ink) 14%, transparent) 0) border-box;
      border: 3px solid transparent;
      border-radius: 22px;
      bottom: 22px;
      box-shadow: 0 18px 46px color-mix(in srgb, var(--shadow) 88%, transparent);
      color: var(--ink);
      cursor: pointer;
      display: flex;
      font: inherit;
      font-size: 22px;
      font-weight: 950;
      height: 54px;
      justify-content: center;
      opacity: 0;
      pointer-events: none;
      position: fixed;
      right: 22px;
      transform: translateY(12px) scale(.94);
      transition: opacity 180ms ease, transform 220ms cubic-bezier(.2, .9, .2, 1);
      width: 54px;
      z-index: 44;
    }

    .scroll-top.visible {
      opacity: 1;
      pointer-events: auto;
      transform: translateY(0) scale(1);
    }

    .scroll-top span {
      background: color-mix(in srgb, var(--accent) 14%, transparent);
      border-radius: 16px;
      display: grid;
      height: 100%;
      place-items: center;
      width: 100%;
    }

    @media (max-width: 640px) {
      .scroll-top {
        bottom: 92px;
        right: 18px;
      }
    }
  `]
})
export class ScrollTopComponent {
  private readonly i18n = inject(I18nService);
  protected readonly scrollY = signal(0);
  protected readonly progress = signal(0);
  protected readonly visible = computed(() => this.scrollY() > 320);
  protected readonly progressPercent = computed(() => Math.round(this.progress() * 100));
  protected readonly label = computed(() => this.i18n.lang() === 'tr' ? 'Sayfanın en üstüne çık' : this.i18n.lang() === 'de' ? 'Zum Seitenanfang' : this.i18n.lang() === 'es' ? 'Volver arriba' : this.i18n.lang() === 'fr' ? 'Retour en haut' : 'Back to top');

  constructor() {
    this.updateProgress();
  }

  @HostListener('window:scroll')
  protected updateProgress(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    const currentY = window.scrollY || 0;
    const maxScroll = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
    this.scrollY.set(currentY);
    this.progress.set(Math.min(1, Math.max(0, currentY / maxScroll)));
  }

  protected scrollTop(): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
